import App, { AppContext, AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { ToastContainer } from 'react-toastify';

import cssVars from 'css-vars-ponyfill';
import NProgress from 'nprogress';

import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import AuthContextProvider from 'contexts/AuthContext';
import BIContextProvider from 'contexts/BIContext';
import { AuthSharedState } from 'contexts/state/AuthState';
import NextApiService from 'services/api';
import { ICustomer } from 'services/customerService';
import { getUserFromToken } from 'utils/token';

import * as gtag from '../utils/gtag';
import '../style/index.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

cssVars({ watch: true });
const { publicRuntimeConfig } = getConfig();

interface IProps extends AppProps, AuthSharedState {}

Router.events.on('routeChangeStart', () => {
  NProgress.start();
});
Router.events.on('routeChangeComplete', (url: string) => {
  gtag.pageview(url);
  NProgress.done();
});
Router.events.on('routeChangeError', () => NProgress.done());

function MyApp({ Component, pageProps, user = undefined, customer }: IProps) {
  const { pathname } = useRouter();
  const isBIReports = pathname.toLowerCase() === '/bi-reports';
  return (
    <>
      <Head>
        <title>Atlas Insurance</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5"
        />
        <meta name="title" content="Atlas Insurance" />
        <meta
          name="description"
          content="Atlas Insurance Claims Management Platform"
        />
        <meta
          name="keywords"
          content="Atlas Insurance, claims, insurance, claims management"
        />
        <meta name="version" content={publicRuntimeConfig.appVersion} />

        {/* Manifest */}
        <meta name="theme-color" content="#0B3D6B" />
        <meta name="robots" content="index,follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://dev-atlas.app.dev-preview.net/"
        />
        <meta property="og:title" content="Atlas Insurance" />
        <meta
          property="og:description"
          content="Atlas Insurance Claims Management Platform"
        />
        {/* <meta property="og:image" content=""/> */}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content="https://dev-atlas.app.dev-preview.net/"
        />
        <meta property="twitter:title" content="Atlas Insurance" />
        <meta
          property="twitter:description"
          content="Atlas Insurance Claims Management Platform"
        />
        {/* <meta property="twitter:image" content=""> */}
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${publicRuntimeConfig.googlePlacesAPIKey}&libraries=places&loading=async`}
          async
        />
      </Head>
      <ToastContainer hideProgressBar />
      <AuthContextProvider user={user} customer={customer}>
        <BIContextProvider showMenu={false} avaliableReportGroups={[]}>
          <Header title="Atlas Insurance" {...pageProps} />
          <Component {...pageProps} />
        </BIContextProvider>
        {!isBIReports && <Footer />}
      </AuthContextProvider>
    </>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const userToken = getUserFromToken(appContext.ctx.req?.headers.cookie);
  const user = userToken ? userToken.data : undefined;

  const api = NextApiService(appContext.ctx.req?.headers.cookie);
  const response = await api
    .post<ICustomer[]>('/auth/customer', {
      $where: {
        ad_oid: user?.oid || '',
      },
    })
    .then(res => res.data);
  const customer = response ? response[0] : ({} as ICustomer);

  return {
    ...appProps,
    user,
    customer,
  };
};

export default MyApp;
